<template>
  <v-card outlined class="card-center pb-2">
    <ValidationObserver ref="searchObserver" v-slot="{ invalid }">
      <v-container class="px-4 py-4" fluid>
        <v-card flat>
          <v-card-title class="py-0">
            {{ $t('ELE0028') }}
          </v-card-title>
          <v-row dense class="mb-1">
            <v-col>
              <TextFieldRef v-if="mode != null" :label="$t('ELE0137')" v-model="searchCondition.inspIndexNo" />
              <TextFieldInput v-else :label="$t('ELE0137')" v-model="searchCondition.inspIndexNo" rules="halfWidth" />
            </v-col>
            <v-spacer />
            <v-col>
              <span d-flex flex-row space-between>
                <BtnSearch @click="onClickSearch" :disabled="mode != null || !canSearch || invalid" />
                <BtnNew class="mx-4" @click="onClickNew" :disabled="mode != null" />
                <BtnReset class="mx-4" @click="onClickResetForm" :disabled="propsExists || mode == null" />
              </span>
            </v-col>
          </v-row>
        </v-card>
      </v-container>
    </ValidationObserver>
    <ValidationObserver ref="observer" v-slot="{ invalid }">
      <v-container v-if="setting" fluid>
        <v-card outlined>
          <v-card-title>
            {{ setting.title }}
            <v-col class="d-flex justify-end">
              <BtnCopy v-if="canCopy" class="mx-4" @click="onClickCopy" />
            </v-col>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="3">
                <OqcIqcInput v-model="editForm.inspTimingCd" required :disabled="!canEditAll" />
              </v-col>
              <v-col cols="3">
                <ManufacturerInput
                  :label="$t('ELE0057')"
                  v-model="editForm.manufacturerCd"
                  required
                  :disabled="!canEditAll"
                  :validOnly="canEditAll"
                />
              </v-col>
              <v-col cols="3">
                <InspCat1Input v-model="editForm.inspCat1" validOnly required :disabled="!canEditAll" />
              </v-col>
              <v-col cols="3">
                <InspCat2Input
                  v-model="editForm.inspCat2"
                  @change="onChangeInspCat2"
                  validOnly
                  required
                  :disabled="!canEditAll && !canEditNonKey"
                  :isStartUp="isStartUp"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <CompanyInput v-if="!canEditAll" v-model="editForm.inspCompanyCd" :label="$t('ELE0048')" disabled />
                <CompanyInput
                  v-else
                  v-model="editForm.inspCompanyCd"
                  :label="$t('ELE0048')"
                  :companyCatCd="companyCatCd"
                  :companyType="companyType"
                  :disabled="isSupplier"
                  validOnly
                  required
                />
              </v-col>
              <v-col cols="3">
                <TextFieldRef v-if="!canEditAll" :label="$t('ELE0049')" :value="editForm.lotNo" />
                <TextFieldInput
                  v-else
                  :label="$t('ELE0049')"
                  validationMode="aggressive"
                  :rules="`lotNoFormat:${formatLotNo}`"
                  v-model="editForm.lotNo"
                  required
                />
              </v-col>
              <v-col cols="6">
                <SupplierPartInput
                  v-model="editForm.partCd"
                  :supplierCd="editForm.manufacturerCd"
                  validOnly
                  required
                  :disabled="!canEditAll"
                  @change="onChangePartCd"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <TextFieldRef
                  v-if="!canEditAll && !canEditNonKey"
                  :label="$t('ELE0050')"
                  :value="editForm.lotQty"
                  right
                />
                <TextFieldInput
                  v-else
                  :label="$t('ELE0050')"
                  v-model="editForm.lotQty"
                  validationMode="aggressive"
                  :debounce="500"
                  rules="naturalNumberQty"
                  right
                  required
                />
              </v-col>
              <v-col cols="3">
                <TextFieldRef v-if="!canEditAll || isMoldManaged" :label="$t('ELE0051')" :value="editForm.moldNo" />
                <TextFieldInput
                  v-else
                  :label="$t('ELE0051')"
                  v-model="editForm.moldNo"
                  rules="halfWidth"
                  maxLength="20"
                />
              </v-col>
              <v-col cols="3">
                <TextFieldRef :label="$t('ELE0052')" :value="editForm.mmmmApplNo" />
              </v-col>
              <v-col cols="3">
                <TextFieldRef v-if="!canEditAll" :label="$t('ELE0053')" :value="editForm.matlSpec" />
                <TextFieldInput
                  v-else
                  :label="$t('ELE0053')"
                  v-model="editForm.matlSpec"
                  rules="halfWidth"
                  maxLength="50"
                  :required="isReqMatlSpec"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <TextAreaRef
                  v-if="!canEditAll && !canEditNonKey"
                  :label="$t('ELE0054')"
                  :value="editForm.remarks"
                  rows="1"
                />
                <TextAreaInput v-else :label="$t('ELE0054')" v-model="editForm.remarks" rows="1" maxLength="1000" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card outlined>
          <v-card-title>
            {{ $t('ELE0142') }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col>
                <InspFilteringInput
                  :label="inspFilteringLabel"
                  v-model="partInfo.inspFilteringGroupCd"
                  validOnly
                  disabled
                />
              </v-col>
              <v-col>
                <TextFieldRef :label="$t('ELE0115')" v-model="partInfo.partInspTimingName" />
              </v-col>
              <v-col>
                <TextFieldRef :label="$t('ELE0144')" v-model="partInfo.inspBasicQty" right />
              </v-col>
              <v-col>
                <AqlRateInput v-model="partInfo.aqlRateCd" validOnly disabled />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card flat>
          <v-card-text>
            <v-row>
              <v-col class="d-flex justify-end">
                <BtnDelete :disabled="!canDelete" @click="onClickDelete" />
                <BtnSave class="mx-4" :disabled="(!canEditAll && !canEditNonKey) || invalid" @click="onClickSave" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-container>
    </ValidationObserver>
    <ConfirmDialog
      :dialog="isConfirmDialog"
      :confirmWord="$t('MSG0013')"
      :btnLabel="$t('ELE0036')"
      @cancel="onCancel"
      @proceed="onProceed"
    />
    <ConfirmDialog
      :dialog="inspIndexDuplicationDialog"
      :confirmWord="$t('MSG0055')"
      :btnLabel="$t('ELE0019')"
      @cancel="onCancel"
      @proceed="saveProcess"
    />
    <InspIndexDialog
      v-if="indexDialog"
      :dialog.sync="indexDialog"
      :mode="copyMode"
      :inspIndexNo="searchCondition.inspIndexNo"
      @close="onClickResetForm"
    />
  </v-card>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import { ModalMode, InspStatusCd, OqcIqcCd, SystemParameterKey, InspectionIndex4M } from '@/enum'
import { API } from '@/plugins/api'
import APIUtils from '@/mixin/api-utils.js'
import Message from '@/mixin/message-utils.js'
import Loading from '@/mixin/loading-utils.js'
import DateUtils from '@/mixin/date-utils.js'
import CheckUtil from '@/mixin/check-utils.js'

import BtnDelete from '@/components/atoms/button/BtnDelete'
import BtnSave from '@/components/atoms/button/BtnSave'
import BtnSearch from '@/components/atoms/button/BtnSearch'
import BtnNew from '@/components/atoms/button/BtnNew'
import BtnReset from '@/components/atoms/button/BtnReset'
import BtnCopy from '@/components/atoms/button/BtnCopy'
import TextFieldInput from '@/components/atoms/base/text-field/TextFieldInput'
import TextFieldRef from '@/components/atoms/base/text-field/TextFieldRef'
import TextAreaInput from '@/components/atoms/base/text-field/TextAreaInput'
import TextAreaRef from '@/components/atoms/base/text-field/TextAreaRef'
import TextFieldInputWithDatePicker from '@/components/atoms/base/text-field/TextFieldInputWithDatePicker'
import SelectSingleInput from '@/components/atoms/base/select/SelectSingleInput'
import CompanyInput from '@/components/atoms/select/CompanyInput'

import ManufacturerInput from '@/components/atoms/select/ManufacturerInput'
import OqcIqcInput from '@/components/atoms/select/OqcIqcInput'
import SupplierPartInput from '@/components/atoms/select/SupplierPartInput'
import InspFilteringInput from '@/components/atoms/select/InspFilteringInput'
import AqlRateInput from '@/components/atoms/select/AqlRateInput'
import InspCat1Input from '@/components/atoms/select/InspCat1Input'
import InspCat2Input from '@/components/atoms/select/InspCat2Input'
import ConfirmDialog from '@/components/organisms/common/dialog/ConfirmDialog'
import InspIndexDialog from '@/components/organisms/terminal/dialog/InspIndexDialog'

export default {
  name: 'EditInspIndexForm',

  mixins: [APIUtils, Message, Loading, DateUtils, CheckUtil],

  components: {
    ValidationObserver,
    BtnDelete,
    BtnSave,
    BtnSearch,
    BtnNew,
    BtnReset,
    BtnCopy,
    TextFieldInput,
    TextFieldRef,
    TextAreaInput,
    TextAreaRef,
    TextFieldInputWithDatePicker,
    SelectSingleInput,
    CompanyInput,
    ManufacturerInput,
    OqcIqcInput,
    SupplierPartInput,
    InspFilteringInput,
    AqlRateInput,
    InspCat1Input,
    InspCat2Input,
    ConfirmDialog,
    InspIndexDialog,
  },

  props: {
    propsInspIndexNo: {
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      isConfirmDialog: false,
      inspIndexDuplicationDialog: false,
      indexDialog: false,
      mode: null,
      copyMode: null,
      propsExists: false,
      formatLotNo: '',
      lotNoFormatRules: {
        normal: '',
        startup: '',
      },
      isReqMatlSpec: false,
      companyCatCd: '',
      companyType: '',
      settings: [
        {
          mode: ModalMode.ADD,
          title: this.$t('ELE0016'),
          url: '/inspIndex/inspIndexes/register',
        },
        {
          mode: ModalMode.EDIT,
          title: this.$t('ELE0017'),
          url: '/inspIndex/inspIndexes/{id}/update',
        },
        {
          mode: ModalMode.INQUIRY,
          title: this.$t('ELE0018'),
          url: '',
        },
        {
          mode: ModalMode.COPY,
          title: this.$t('ELE0251'),
          url: '/inspIndex/inspIndexes/{id}/copy',
        },
      ],
      searchCondition: {
        inspIndexNo: '',
      },
      editForm: {
        inspTimingCd: '',
        manufacturerCd: '',
        lotNo: '',
        partCd: '',
        inspCat1: '',
        inspCat2: '',
        lotQty: '',
        inspCompanyCd: this.$store.getters.companyCd,
        moldNo: '',
        mmmmApplNo: '',
        matlSpec: '',
        remarks: '',
        versionNo: null,
      },
      moldManaged: false,
      isInspIndex4M: false,
      partInfo: {
        inspFilteringGroupCd: '',
        partInspTimingName: '',
        inspBasicQty: '',
        aqlRateCd: '',
        partRev: '',
      },
    }
  },

  computed: {
    isSupplier() {
      return this.$store.getters.isSupplier
    },
    setting: {
      get() {
        return this.settings.find((el) => el.mode === this.mode)
      },
    },
    canDelete: {
      get() {
        return this.setting.mode === ModalMode.EDIT && this.editForm.inspStatusCd == InspStatusCd.WAITING_FOR
      },
    },
    canEditAll: {
      get() {
        // 全項目編集できる条件
        return (
          this.setting.mode === ModalMode.ADD ||
          (this.setting.mode === ModalMode.EDIT && this.editForm.inspStatusCd == InspStatusCd.WAITING_FOR)
        )
      },
    },
    canEditNonKey: {
      get() {
        // 検査中でも、key以外の一部項目のみ編集を許可する条件
        let result = false
        if (this.setting.mode === ModalMode.EDIT) {
          result = true
        }
        return result
      },
    },
    canCopy: {
      get() {
        // コピー可能な条件
        return this.setting.mode === ModalMode.INQUIRY || this.setting.mode === ModalMode.EDIT
      },
    },
    isMoldManaged: {
      get() {
        return this.moldManaged
      },
    },
    canSearch: {
      get() {
        let result = false
        if (this.searchCondition.inspIndexNo.length) {
          result = true
        }
        return result
      },
    },
    inspFilteringLabel: {
      get() {
        let result = ''
        if (this.$store.getters.canOQCOnly === true) {
          result = this.$t('ELE0289')
        } else {
          result = this.$t('ELE0290')
        }
        return result
      },
    },
    isStartUp: {
      // inspCat2の選択肢制限
      get() {
        let result = null
        if (this.setting.mode === ModalMode.EDIT && this.editForm.inspStatusCd == InspStatusCd.IN_PROGRESS) {
          result = this.editForm['m_inspCat2'].isStartUp
        }
        return result
      },
    },
  },

  methods: {
    async search(inspIndexNo) {
      this.searchCondition.inspIndexNo = inspIndexNo
      await this.$nextTick()
      await this.onClickSearch()
    },
    async onClickSearch() {
      try {
        this.startLoading()
        if (!(await this.$refs.searchObserver.validate())) {
          return
        }

        const params = {
          userCompanyCd: this.$store.getters.companyCd,
        }
        const url = '/inspIndex/inspIndexes/' + this.searchCondition.inspIndexNo

        const element = await API.get(url, params)
        this.editForm = element
        this.partInfo = {
          partInspTimingName: element['m_partInspTiming'].partInspTimingName,
          inspBasicQty: element['m_part'].inspBasicQty,
          aqlRateCd: element['m_partAqlRate'].aqlRateCd,
          partRev: element['m_part'].partRev,
        }
        this.formatLotNo = element['m_inspCat2'].isStartUp
          ? this.lotNoFormatRules.startup
          : this.lotNoFormatRules.normal
        this.partInfo.inspFilteringGroupCd = element['m_partInspFiltering'].inspFilteringGroupCd
        this.editForm.lotQty = element['t_lot'].lotQty

        this.moldManaged = element['m_part'].isManagedMold
        this.isInspIndex4M = element['mmmmApplNo'] == InspectionIndex4M.NOT_STARTED_YET
        if (this.moldManaged) {
          this.editForm.moldNo = element['m_part'].moldNo
        }
        if (
          element.inspStatusCd !== InspStatusCd.COMPLETE &&
          this.isInspectableCompany(element.inspCompanyCd, element['m_company'].companyCatCd)
        ) {
          this.mode = ModalMode.EDIT
        } else {
          this.mode = ModalMode.INQUIRY
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0118') })))
      } finally {
        this.stopLoading()
      }
    },
    onClickNew() {
      this.mode = ModalMode.ADD
      this.searchCondition.inspIndexNo = ''
    },
    onClickResetForm() {
      if (this.propsExists) {
        setTimeout(() => {
          this.$router.back()
        }, 500)
        return
      }
      this.mode = null
      this.searchCondition.inspIndexNo = ''
      this.inspIndexDuplicationDialog = false
      this.resetForm()
    },
    async onChangePartCd() {
      try {
        this.startLoading()
        if (!this.editForm.partCd) {
          return
        }
        const params = {}
        if (this.$store.getters.canOQCOnly === true) {
          params.inspTimingCd = OqcIqcCd.OQC
        } else params.inspTimingCd = OqcIqcCd.IQC
        const url = `/master/parts/${this.editForm.partCd}/info`
        const element = await API.get(url, params)
        this.partInfo = {
          partInspTimingName: element.partInspTimingName,
          inspBasicQty: element.inspBasicQty,
          aqlRateCd: element.aqlRateCd,
          inspFilteringGroupCd: element.inspFilteringGroupCd,
        }
        if (
          (element.isManagedMold && !this.moldManaged) ||
          (!element.isManagedMold && this.moldManaged) ||
          element.isManagedMold
        ) {
          this.editForm.moldNo = element.moldNo
        }
        this.moldManaged = element.isManagedMold
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0059') })))
      } finally {
        this.stopLoading()
      }
    },
    onClickCopy() {
      this.copyMode = ModalMode.COPY
      this.indexDialog = true
    },
    async onClickSave() {
      this.startLoading()
      try {
        const params = {
          inspTimingCd: this.editForm.inspTimingCd,
          manufacturerCd: this.editForm.manufacturerCd,
          lotNo: this.editForm.lotNo,
          partCd: this.editForm.partCd,
          inspCompanyCd: this.editForm.inspCompany,
        }
        const element = await API.get('/inspIndex/lot/complete', params)
        if (element) {
          this.inspIndexDuplicationDialog = true
        } else {
          this.saveProcess()
        }
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0118') })))
      } finally {
        this.stopLoading()
      }
    },
    async saveProcess() {
      this.startLoading()
      try {
        if (!(await this.$refs.observer.validate())) {
          return
        }
        const body = {
          userCompanyCd: this.$store.getters.companyCd,
          items: {
            inspTimingCd: this.editForm.inspTimingCd,
            manufacturerCd: this.editForm.manufacturerCd,
            lotNo: this.editForm.lotNo,
            partCd: this.editForm.partCd,
            inspCat1: this.editForm.inspCat1,
            inspCat2: this.editForm.inspCat2,
            lotQty: this.editForm.lotQty,
            inspCompanyCd: this.editForm.inspCompanyCd,
            moldNo: this.editForm.moldNo,
            mmmmApplNo: this.editForm.mmmmApplNo,
            matlSpec: this.editForm.matlSpec,
            remarks: this.editForm.remarks,
            versionNo: this.editForm.versionNo,
          },
        }
        const url = this.setting.url.replace('{id}', this.searchCondition.inspIndexNo)
        await API.post(url, body)
        this.setSuccessMessage(this.$t('MSG0011', { p0: this.$t('ELE0118') }))
        this.onClickResetForm()
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0003', { p0: this.$t('ELE0118') })))
      } finally {
        this.stopLoading()
      }
    },
    onClickDelete() {
      this.isConfirmDialog = true
    },
    onCancel() {
      this.isConfirmDialog = false
      this.inspIndexDuplicationDialog = false
    },
    async onProceed() {
      this.startLoading()
      try {
        this.isConfirmDialog = false

        const url = '/inspIndex/inspIndexes/' + this.searchCondition.inspIndexNo + '/delete'
        const body = {
          versionNo: this.editForm.versionNo,
        }
        await API.post(url, body)
        this.setSuccessMessage(this.$t('MSG0014'))
        this.onClickResetForm()
      } catch (error) {
        this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0004', { p0: this.$t('ELE0118') })))
      } finally {
        this.stopLoading()
      }
    },
    resetForm() {
      this.editForm = {
        inspTimingCd: '',
        manufacturerCd: '',
        lotNo: '',
        partCd: '',
        inspCat1: '',
        inspCat2: '',
        lotQty: '',
        inspCompanyCd: this.$store.getters.companyCd,
        moldNo: '',
        mmmmApplNo: '',
        matlSpec: '',
        remarks: '',
        versionNo: null,
      }
      this.partInfo = {
        inspFilteringGroupCd: '',
        partInspTimingName: '',
        inspBasicQty: '',
        aqlRateCd: '',
        partRev: '',
      }
    },
    async onChangeInspCat2(inspCatItem) {
      if (inspCatItem) {
        this.formatLotNo = inspCatItem.isStartUp ? this.lotNoFormatRules.startup : this.lotNoFormatRules.normal
        this.isInspIndex4M = inspCatItem.isMmmm
        this.editForm.mmmmApplNo = this.isInspIndex4M ? InspectionIndex4M.NOT_STARTED_YET : ''
      }
    },
    async setFormatLotNo() {
      try {
        this.startLoading()
        const param = await API.get(`/master/systemParameters?key=FORMAT_LOT`)
        param?.forEach((element) => {
          if (element.key == SystemParameterKey.FORMAT_LOT_NO) {
            this.lotNoFormatRules.normal = element.value
            this.formatLotNo = element.value
          }
          if (element.key == SystemParameterKey.FORMAT_LOT_STARTUP) {
            this.lotNoFormatRules.startup = element.value
          }
        })
      } catch (error) {
        this.formatLotNo = ''
        if (error.response.status != 404) {
          this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0348') })))
        }
      } finally {
        this.stopLoading()
      }
    },
    async setMatlSpecReq() {
      let result = ''
      try {
        this.startLoading()
        const param = await API.get(`/master/systemParameter/${SystemParameterKey.MATL_SPEC_REQ}`)
        result = param.value
      } catch (error) {
        result = ''
        if (error.response.status != 404) {
          this.setErrorMessage(this.resolveErrorMessage(error, this.$t('MSG0002', { p0: this.$t('ELE0348') })))
        }
      } finally {
        if (result == 'TRUE') {
          this.isReqMatlSpec = true
        }
        this.stopLoading()
      }
    },
  },

  async mounted() {
    await this.setFormatLotNo()
    await this.setMatlSpecReq()

    if (this.propsInspIndexNo) {
      this.propsExists = true
      this.search(this.propsInspIndexNo)
    }
  },
}
</script>
